import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { queryKeys } from "@/lib/queryKeys";
import { TaskCalendar } from "@/components/TaskCalendar";
import { SegmentedToggle } from "@/components/SegmentedToggle";
import { Skeleton } from "@/components/Skeleton";
import { EmptyState } from "@/components/EmptyState";

/**
 * Calendar view — month/week grid placing tasks on their due date with
 * drag-to-reschedule. Sits beside the board/list/table leaf routes under the
 * shared project shell.
 */
function CalendarView() {
  const { projectId } = Route.useParams();
  const navigate = useNavigate();

  const tasksQ = useQuery({
    queryKey: queryKeys.tasksFor(projectId),
    queryFn: () => api.listTasks(projectId),
  });
  const teamQ = useQuery({ queryKey: queryKeys.team(), queryFn: api.listTeam });

  const users = useMemo(() => {
    const map = new Map<string, { name: string; image: string | null }>();
    for (const m of teamQ.data?.members ?? []) {
      map.set(m.id, {
        name: m.name ?? m.email ?? m.id.slice(0, 8),
        image: m.image ?? null,
      });
    }
    return map;
  }, [teamQ.data]);

  return (
    <div className="proj-shell-content">
      <div className="board-bar" style={{ marginBottom: 12 }}>
        <SegmentedToggle
          options={[
            { value: "board", label: "Board", icon: "board" },
            { value: "list", label: "List", icon: "clip" },
            { value: "table", label: "Table", icon: "grid" },
            { value: "calendar", label: "Calendar", icon: "cal" },
            { value: "timeline", label: "Timeline", icon: "arrow" },
          ]}
          value="calendar"
          onChange={(v) =>
            navigate({
              to:
                v === "list"
                  ? "/projects/$projectId/list"
                  : v === "table"
                    ? "/projects/$projectId/table"
                    : v === "timeline"
                      ? "/projects/$projectId/timeline"
                      : v === "calendar"
                        ? "/projects/$projectId/calendar"
                        : "/projects/$projectId",
              params: { projectId },
            })
          }
        />
      </div>

      {tasksQ.isLoading ? (
        <Skeleton count={6} height={60} />
      ) : tasksQ.isError ? (
        <p className="t-muted" style={{ padding: 24 }}>
          Couldn&rsquo;t load tasks for the calendar. Try again.
        </p>
      ) : (tasksQ.data?.tasks ?? []).length === 0 ? (
        <EmptyState
          icon="cal"
          title="Nothing scheduled yet"
          caption="Tasks with a due date appear on the calendar. Add a due date to a task to see it here."
        />
      ) : (
        <TaskCalendar
          projectId={projectId}
          tasks={tasksQ.data?.tasks ?? []}
          users={users}
        />
      )}
    </div>
  );
}

export const Route = createFileRoute("/projects/$projectId/calendar")({
  component: CalendarView,
});
